import { Config } from "../../Config";
import { BufferUsage } from "../RenderEngine/RenderEnum/BufferTargetType";
import { DrawType } from "../RenderEngine/RenderEnum/DrawType";
import { IndexFormat } from "../RenderEngine/RenderEnum/IndexFormat";
import { MeshTopology } from "../RenderEngine/RenderEnum/RenderPologyMode";
import { Shader3D } from "../RenderEngine/RenderShader/Shader3D";
import { VertexMesh } from "../RenderEngine/RenderShader/VertexMesh";
import { VertexDeclaration } from "../RenderEngine/VertexDeclaration";
import { LayaGL } from "../layagl/LayaGL";
import { VertexElement } from "../renders/VertexElement";
import { VertexElementFormat } from "../renders/VertexElementFormat";
import { Resource } from "./Resource";
export class VertexMesh2D {
    static getVertexDeclaration(vertexFlags, compatible = true) {
        let verDecs = [];
        for (let i = 0, len = vertexFlags.length; i < len; i++) {
            let vertexFlag = vertexFlags[i];
            let verDec = VertexMesh2D._vertexDeclarationMap[vertexFlag + (compatible ? "_0" : "_1")];
            if (!verDec) {
                var subFlags = vertexFlag.split(",");
                var offset = 0;
                var elements = [];
                for (let j = 0, n = subFlags.length; j < n; j++) {
                    var element;
                    switch (subFlags[j]) {
                        case "POSITION":
                            element = new VertexElement(offset, VertexElementFormat.Vector3, VertexMesh.MESH_POSITION0);
                            offset += 12;
                            break;
                        case "COLOR":
                            element = new VertexElement(offset, VertexElementFormat.Vector4, VertexMesh.MESH_COLOR0);
                            offset += 16;
                            break;
                        case "UV":
                            element = new VertexElement(offset, VertexElementFormat.Vector2, VertexMesh.MESH_TEXTURECOORDINATE0);
                            offset += 8;
                            break;
                        case "BLENDWEIGHT":
                            element = new VertexElement(offset, VertexElementFormat.Vector4, VertexMesh.MESH_BLENDWEIGHT0);
                            offset += 16;
                            break;
                        case "BLENDINDICES":
                            if (compatible) {
                                element = new VertexElement(offset, VertexElementFormat.Vector4, VertexMesh.MESH_BLENDINDICES0);
                                offset += 16;
                            }
                            else {
                                element = new VertexElement(offset, VertexElementFormat.Byte4, VertexMesh.MESH_BLENDINDICES0);
                                offset += 4;
                            }
                            break;
                        default:
                            throw "VertexMesh: unknown vertex flag.";
                    }
                    elements.push(element);
                }
                verDec = new VertexDeclaration(offset, elements);
                VertexMesh2D._vertexDeclarationMap[vertexFlag + (compatible ? "_0" : "_1")] = verDec;
            }
            verDecs.push(verDec);
        }
        return verDecs;
    }
    static getMeshDefine(mesh, out) {
        out.length = 0;
        let vertexs = mesh._vertexBuffers;
        for (var i = 0, n = vertexs.length; i < n; i++) {
            let elements = vertexs[i].vertexDeclaration._vertexElements;
            for (const element of elements) {
                switch (element.elementUsage) {
                    case VertexMesh.MESH_COLOR0:
                        out.push(Shader3D.getDefineByName("COLOR"));
                        break;
                    case VertexMesh.MESH_TEXTURECOORDINATE0:
                        out.push(Shader3D.getDefineByName("UV"));
                        break;
                }
            }
        }
    }
}
VertexMesh2D._vertexDeclarationMap = {};
export class Mesh2D extends Resource {
    static createMesh2DByPrimitive(vbs, vbDeclaration, ib, ibFormat, submeshInfo, canRead = false) {
        let mesh2d = new Mesh2D();
        mesh2d.canRead = canRead;
        let vbArray = [];
        let vertices = [];
        for (var i = 0, n = vbs.length; i < n; i++) {
            let vbdata = vbs[i];
            let vertex = LayaGL.renderDeviceFactory.createVertexBuffer(BufferUsage.Dynamic);
            vertex.vertexDeclaration = vbDeclaration[i];
            vertex.setDataLength(vbdata.buffer.byteLength);
            vertex.setData(vbdata.buffer, 0, 0, vbdata.buffer.byteLength);
            vbArray.push(vertex);
            vertices[i] = vbdata.buffer;
        }
        let indexBuffer = LayaGL.renderDeviceFactory.createIndexBuffer(BufferUsage.Dynamic);
        indexBuffer._setIndexDataLength(ib.buffer.byteLength);
        indexBuffer._setIndexData(ib, 0);
        indexBuffer.indexCount = ib.length;
        indexBuffer.indexType = ibFormat;
        mesh2d._setBuffers(vbArray, indexBuffer);
        let geometryArray = [];
        for (var i = 0; i < submeshInfo.length; i++) {
            let geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
            geometry.bufferState = mesh2d._bufferState;
            geometry.setDrawElemenParams(submeshInfo[i].length, submeshInfo[i].start);
            geometry.indexFormat = ibFormat;
            geometryArray.push(geometry);
        }
        mesh2d._setSubMeshes(geometryArray);
        if (canRead) {
            mesh2d._vertices = vertices;
            mesh2d._indices = ib;
        }
        return mesh2d;
    }
    get vertexBuffers() {
        return this._vertexBuffers;
    }
    get indexBuffer() {
        return this._indexBuffer;
    }
    get vertexCount() {
        return this._vertexCount;
    }
    get indexCount() {
        return this._indexBuffer.indexCount;
    }
    get subMeshCount() {
        return this._subMeshes.length;
    }
    get indexFormat() {
        return this._indexFormat;
    }
    constructor() {
        super();
        this._instanceBufferStateType = 0;
        this._vertexBuffers = null;
        this._indexBuffer = null;
        this._vertexCount = 0;
        this._indexFormat = IndexFormat.UInt16;
        this.canRead = false;
        this._vertices = null;
        this._indices = null;
        this._bufferState = LayaGL.renderDeviceFactory.createBufferState();
        this._subMeshes = [];
        this.destroyedImmediately = Config.destroyResourceImmediatelyDefault;
    }
    _disposeResource() {
        for (let i = 0, n = this._subMeshes.length; i < n; i++)
            this._subMeshes[i].destroy();
        for (let i = 0, n = this._vertexBuffers.length; i < n; i++)
            this._vertexBuffers[i].destroy();
        this._indexBuffer && this._indexBuffer.destroy();
        this._bufferState.destroy();
        this._instanceBufferState && this._instanceBufferState.destroy();
        this._instanceWorldVertexBuffer && this._instanceWorldVertexBuffer.destroy();
        this._instanceSimpleAniVertexBuffer && this._instanceSimpleAniVertexBuffer.destroy();
        this._setCPUMemory(0);
        this._setGPUMemory(0);
        this._bufferState = null;
        this._instanceBufferState = null;
        this._vertexBuffers = null;
        this._indexBuffer = null;
        this._subMeshes = null;
        this._indexBuffer = null;
    }
    _setSubMeshes(subMeshes) {
        this._subMeshes = subMeshes;
    }
    _setBuffers(vertexBuffers, indexBuffer) {
        var bufferState = this._bufferState;
        this._vertexBuffers = vertexBuffers;
        this._indexBuffer = indexBuffer;
        bufferState.applyState(vertexBuffers, indexBuffer);
    }
    getSubMesh(index) {
        return this._subMeshes[index];
    }
    setVertices(vertices) {
        for (let i = 0, len = vertices.length; i < len; i++) {
            if (vertices[i] && this._vertexBuffers[i]) {
                this._vertexBuffers[i].setData(vertices[i], 0, 0, vertices[i].byteLength);
            }
        }
        if (this.canRead) {
            this._vertices = vertices;
        }
    }
    getVertices() {
        if (!this.canRead || !this._vertices) {
            throw new Error("Can't getVertices without the canRead flag, or if the canRead flag is false before setVertices!");
        }
        else {
            return this._vertices;
        }
    }
    setVertexByIndex(data, index, bufferOffset = 0) {
        this._vertexBuffers[index].setData(data, bufferOffset, 0, data.byteLength);
    }
    setIndices(indices) {
        var format;
        if (indices instanceof Uint32Array)
            format = IndexFormat.UInt32;
        else if (indices instanceof Uint16Array)
            format = IndexFormat.UInt16;
        else if (indices instanceof Uint8Array)
            format = IndexFormat.UInt8;
        let indexBuffer = this._indexBuffer;
        if (indexBuffer.indexCount < indices.length) {
            console.error("Mesh2D:set indices buffer large than ori indices");
        }
        var iscreateBuffer = false;
        if (this._indexFormat !== format || indexBuffer.indexCount < indices.length) {
            indexBuffer.destroy();
            iscreateBuffer = true;
            this._indexBuffer = indexBuffer = LayaGL.renderDeviceFactory.createIndexBuffer(BufferUsage.Static);
            this._indexBuffer._setIndexDataLength(indices.byteLength);
            indexBuffer.indexCount = indices.length;
            indexBuffer.indexType = format;
        }
        indexBuffer._setIndexData(indices, 0);
        if (iscreateBuffer) {
            this._bufferState.applyState(this._bufferState._vertexBuffers, indexBuffer);
        }
        if (this.canRead) {
            this._indices = indices;
        }
    }
    getIndices() {
        if (!this.canRead || !this._indices) {
            throw new Error("Can't getIndices without the canRead flag, or if the canRead flag is false before setIndices!");
        }
        else {
            return this._indices;
        }
    }
}
